import type { UserAndOrgBaseProps } from './RegistrationPage.js'
import type React from 'react';
import { useEffect, useRef } from 'react'

/* Mirrors the @Size cap on RegistrationPayload.comment, so the field stops rather than the request. */
const MAX_COMMENT_LENGTH = 2000

interface RegistrationFormProps {
  baseProps: UserAndOrgBaseProps
  setBaseProps:  React.Dispatch<React.SetStateAction<UserAndOrgBaseProps>>
  formRef: React.RefObject<HTMLFormElement | null>
}

export const RegistrationForm = (props: RegistrationFormProps) => {
  const repeatRef = useRef<HTMLInputElement>(null)

  const updateSlug = (name: string) => {
    const generated = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    props.setBaseProps(prev => ({...prev, slug: generated}))
  }

  useEffect(() => {
    if (repeatRef.current && props.baseProps.password && props.baseProps.passwordRepeat) {
      repeatRef.current.setCustomValidity(
        props.baseProps.password !== props.baseProps.passwordRepeat ? "Passwords do not match." : ''
      )
    }
  }, [props.baseProps.password, props.baseProps.passwordRepeat])

  return (
    <form ref={props.formRef} className="space-y-4" noValidate>
      <label className="block font-semibold">Email</label>
      <input
        type="email"
        required
        value={props.baseProps.email || ''}
        onChange={(e) => props.setBaseProps((prev) => ({ ...prev, email: e.target.value }))}
        className="input input-bordered w-full"
      />

      <label className="block font-semibold">First Name</label>
      <input
        type="text"
        required
        minLength={2}
        value={props.baseProps.firstname || ''}
        onChange={(e) => props.setBaseProps((prev) => ({ ...prev, firstname: e.target.value }))}
        className="input input-bordered w-full"
      />

      <label className="block font-semibold">Last Name</label>
      <input
        type="text"
        required
        minLength={2}
        value={props.baseProps.lastname || ''}
        onChange={(e) => props.setBaseProps((prev) => ({ ...prev, lastname: e.target.value }))}
        className="input input-bordered w-full"
      />

      <label className="block font-semibold">Organization Name</label>
      <input
        type="text"
        required
        value={props.baseProps.orgName || ''}
        onChange={(e) => {
          const name = e.target.value
          props.setBaseProps((prev) => ({ ...prev, orgName: name }))
          updateSlug(name)
        }}
        className="input input-bordered w-full"
      />

      <div className="text-sm text-gray-500">
        Slug preview: <code>{props.baseProps.slug || 'n/a'}</code>
      </div>

      <label className="block font-semibold">Password</label>
      <input
        type="password"
        required
        minLength={8}
        value={props.baseProps.password || ''}
        onChange={(e) => props.setBaseProps((prev) => ({ ...prev, password: e.target.value }))}
        className="input input-bordered w-full"
      />

      <label className="block font-semibold">Repeat Password</label>
      <input
        ref={repeatRef}
        type="password"
        required
        minLength={8}
        value={props.baseProps.passwordRepeat || ''}
        onChange={(e) => props.setBaseProps((prev) => ({ ...prev, passwordRepeat: e.target.value }))}
        className="input input-bordered w-full"
        onInput={(e) =>
          e.currentTarget.setCustomValidity(
            e.currentTarget.value !== props.baseProps.password ? 'Passwords do not match.' : ''
          )
        }
      />

      <label className="block font-semibold">
        Anything you would like to tell us? <span className="font-normal text-gray-500">(optional)</span>
      </label>
      <textarea
        rows={3}
        maxLength={MAX_COMMENT_LENGTH}
        value={props.baseProps.comment || ''}
        onChange={(e) => props.setBaseProps((prev) => ({ ...prev, comment: e.target.value }))}
        className="textarea textarea-bordered w-full"
        placeholder="What you plan to use spcfy for, who referred you, or anything else we should know."
      />
    </form>
  )
}